import { Stack, Typography, useTheme } from "@mui/material";
import { FileText } from "lucide-react";
import type { FC } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PortfolioItemIcon } from "web_myplann/Portfolio/Components/PortfolioItemIcon";
import { PinCandidateRow } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/PinCandidateRow";
import type {
	PinCandidate,
	PinKind,
} from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/pinKind";
import { usePinCandidateRows } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/usePinCandidateRows";
import { DocumentRowSkeleton } from "web_myplann/Portfolio/Components/Redesign/Documents/DocumentRowSkeleton";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";

const SKELETON_ROWS = 5;

type Props = {
	kind: PinKind;
	open: boolean;
	onClose: () => void;
	candidates: PinCandidate[];
	isLoading: boolean;
	isPending: boolean;
	onSelect: (candidate: PinCandidate) => void;
};

export const PinExistingDrawer: FC<Props> = ({
	kind,
	open,
	onClose,
	candidates,
	isLoading,
	isPending,
	onSelect,
}) => {
	const theme = useTheme();
	const { palette, iconSize } = theme;
	const { t } = useResource();
	const kindResources = Resources.PortfolioDetail.Pin.Kind[kind];

	const rows = usePinCandidateRows(candidates);

	return (
		<MyPlannDrawer
			anchor="bottom"
			open={open}
			onClose={onClose}
			title={t(kindResources.selectTitle)}
		>
			<Stack pb={theme.spacingTokens.xl}>
				{isLoading ? (
					Array.from({ length: SKELETON_ROWS }).map((_, index) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton list
						<DocumentRowSkeleton key={index} />
					))
				) : rows.length === 0 ? (
					<Stack
						px={theme.spacingTokens.lg}
						py={theme.spacingTokens.lg}
						alignItems="center"
					>
						<Typography
							variant="body2"
							color={palette.grey[500]}
							textAlign="center"
						>
							{t(kindResources.selectEmpty)}
						</Typography>
					</Stack>
				) : (
					rows.map((row, index) => (
						<PinCandidateRow
							key={row.key}
							row={row}
							icon={
								row.candidate.kind === "document" ? (
									<FileText size={iconSize.md} color={palette.grey[500]} />
								) : (
									<PortfolioItemIcon
										domain={row.candidate.data.domain}
										propertyType={row.candidate.data.type}
									/>
								)
							}
							iconBg={
								row.candidate.kind === "document"
									? palette.grey[100]
									: undefined
							}
							isLast={index === rows.length - 1}
							disabled={isPending}
							onClick={() => onSelect(row.candidate)}
						/>
					))
				)}
			</Stack>
		</MyPlannDrawer>
	);
};
